import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { validateEmail } from '../../utils/validation';
import { encodeImpersonationPayload } from '../../utils/authMappers';
import SearchableSelect from '../../components/shared/SearchableSelect';
import StatusFilterTabs from '../../components/shared/StatusFilterTabs';
import { GridExportButton, GridSearchInput } from '../../components/shared/GridToolbar';
import {
  GridColumnFilterButton,
  GridColumnFilterPanel,
} from '../../components/shared/GridColumnFilterPanel';
import { type ActiveGridFilter } from '../../utils/gridColumnFilters';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { commonTextFilterParams } from '../../lib/agGridOptions';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DataGrid, type GridApi } from '../../components/ui/table';
import { ActiveGridFiltersBar } from '../../components/shared/agGridCells';
import {
  buildUserGridRows,
  buildUserColumnFilterFields,
  createUserColumnDefs,
  getUserDetailRowHeight,
  UserDetailCellRenderer,
  type UserGridContext,
  type UserGridRow,
  USER_COLUMN_LABELS,
} from './table/columnDefs';
import { GRID_PAGE_SIZE, GRID_PAGE_SIZE_SELECTOR, userGridOptionsBase } from './table/gridOptions';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { Switch } from '../../components/ui/switch';
import { cn } from '../../lib/utils';
import {
  ENTITY_STATUS_SELECT_OPTIONS,
  ENTITY_STATUS_TABS,
  matchesEntityStatus,
} from '../../utils/status';
import type { ProvisionUserRequest, RoleOption } from '../roles/models/types';
import { type MerchantOption, type PlatformUserListResponse, type PlatformUserRow, type PlatformUserUpdateRequest, type RecentLogin, type UserColumnFilterKey, type UserColumnFilters, type UserModalMode } from './models/types';
import { EMPTY_USER_COLUMN_FILTERS } from './constants/constants';
import {
  fetchActiveMerchants,
  fetchPlatformUsers,
  fetchRecentLogins,
  fetchRolesList,
  fetchStatsPlatformUsers,
  impersonateUser,
  normalizePlatformUsers,
  provisionUser,
  updatePlatformUser,
} from './api/api';
import { buildPlatformUserRequest, formatRelativeTime, getDefaultMerchantRoleId, isAdminScopedRole, isMerchantScopedRole, isSuperAdminRow, mapAgGridFilterModelToUserFilters, resolveMerchantIdForCompany, roleStyle, toApiStatus, toRoleSelectOptions, userRowKey } from './utils/utils';
import { downloadExcelExport, EXPORT_TYPES } from '../../utils/backendExport';
import SearchableMultiSelect from '../../components/shared/SearchableMultiSelect';
import { ACCENT_COLORS, EMPTY_USER_FORM, SEARCH_DEBOUNCE_MS } from './constants/constants';
import {
  LIST_CONSOLE_AG_GRID_CLASS,
  ListConsoleGridSection,
  ListConsolePage,
} from '../../components/shared/ListConsoleLayout';
import '../transaction-monitoring/TransactionMonitoring.css';
import { USER_COLUMN_FILTER_KEYS } from './constants/constants';
import { BsPeopleFill, BsCheckCircle, BsShieldFill, BsEnvelopeFill, BsBuilding, BsPlusCircle, BsShop, BsEye, BsEyeSlash } from 'react-icons/bs';
import { MERCHANT_DASHBOARD_KEY_OPTIONS, resolveMerchantDashboardKey } from '../../utils/merchantDashboardKey';
import { apiService } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import './UserManagement.css';

const USER_GRID_DEFAULT_COL_DEF = {
  suppressAutoSize: true,
  filterParams: {
    ...commonTextFilterParams,
    filterOptions: ['contains'],
  },
};

const UserManagement: React.FC = () => {
  const [users, setUsers]             = useState<PlatformUserRow[]>([]);
  const [statsUsers, setStatsUsers]   = useState<PlatformUserRow[]>([]);
  const [loading, setLoading]         = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [listError, setListError]     = useState<string | null>(null);
  const [totalAdmins, setTotalAdmins] = useState(0);
  const [totalMerchants, setTotalMerchants] = useState(0);
  const [userModalMode, setUserModalMode] = useState<UserModalMode | null>(null);
  const [userForm, setUserForm] = useState({ ...EMPTY_USER_FORM });
  const [modalRoles, setModalRoles] = useState<RoleOption[]>([]);
  const [filterRoles, setFilterRoles] = useState<RoleOption[]>([]);
  const [rolesListLoading, setRolesListLoading] = useState(false);
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [merchantsLoading, setMerchantsLoading] = useState(false);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSaving, setModalSaving] = useState(false);
  const [togglingUserKey, setTogglingUserKey] = useState<string | null>(null);
  const [togglingSandboxKey, setTogglingSandboxKey] = useState<string | null>(null);
  const [impersonatingKey, setImpersonatingKey] = useState<string | null>(null);
  const [expandedRowKey, setExpandedRowKey] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [recentLoginsByKey, setRecentLoginsByKey] = useState<Record<string, RecentLogin[]>>({});
  const [recentLoginsLoadingKey, setRecentLoginsLoadingKey] = useState<string | null>(null);
  const [recentLoginsError, setRecentLoginsError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch]           = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterRoleId, setFilterRoleId] = useState('All');
  const [columnFilters, setColumnFilters] = useState<UserColumnFilters>(
    EMPTY_USER_COLUMN_FILTERS,
  );
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const [gridFilterEpoch, setGridFilterEpoch] = useState(0);
  const skipRefetchRef = useRef(false);
  const gridApiRef = useRef<GridApi<UserGridRow> | null>(null);

  const authUser = useAuthStore(state => state.user);
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);
  // const isMerchantUser = authUser?.userType === 'MERCHANT';
  // const authMerchantId = authUser?.merchantId && authUser.merchantId > 0
  //   ? String(authUser.merchantId)
  //   : '';

  const isCreateMode = userModalMode === 'create';
  const isEditMode = userModalMode === 'edit';
  const selectedModalRole = modalRoles.find(r => r.autoId === userForm.roleId) ?? null;
  const isReferralEligibleRole = isAdminScopedRole(selectedModalRole);
  const needsMerchantId =  isMerchantScopedRole(selectedModalRole);
  // Only a true super admin may ever view/change another user's 2FA setting: mirrors the
  // backend's TwoFactorAuthorizationSupport gate, which rejects this field from anyone else.
  const isSuperAdminCaller = String(authUser?.adminType ?? authUser?.role ?? '').trim().toUpperCase() === 'SUPER_ADMIN';
  const tableFiltersRef = useRef({
    filterStatus,
    filterRoleId,
    search: debouncedSearch,
    columnFilters,
  });

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedSearch(search);
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeout);
  }, [search]);

  const handleRemoveColumnFilter = useCallback((key: string) => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        [key]: null,
      },
    };
    setColumnFilters((prev) => ({
      ...prev,
      [key]: null,
    }));
    const api = gridApiRef.current;
    if (api?.getColumnFilterModel(key)) {
      skipRefetchRef.current = true;
      void api.setColumnFilterModel(key, null).then(() => {
        api.onFilterChanged();
      });
    }
  }, []);

  const handleClearColumnFilters = useCallback(() => {
    tableFiltersRef.current = {
      ...tableFiltersRef.current,
      columnFilters: EMPTY_USER_COLUMN_FILTERS,
    };
    setColumnFilters(EMPTY_USER_COLUMN_FILTERS);
    const api = gridApiRef.current;
    if (api && Object.keys(api.getFilterModel() ?? {}).length > 0) {
      api.setFilterModel(null);
      api.onFilterChanged();
    }
  }, []);

  const handleApplyColumnFilters = useCallback(
    (values: Record<string, string[] | null>) => {
      gridApiRef.current?.setFilterModel(null);
      setColumnFilters({
        ...EMPTY_USER_COLUMN_FILTERS,
        ...(Object.fromEntries(
          USER_COLUMN_FILTER_KEYS.map((key) => [
            key,
            values[key]?.length ? values[key] : null,
          ]),
        ) as UserColumnFilters),
      });
    },
    [],
  );

  const applyPlatformUserResponse = useCallback(
    (data: PlatformUserListResponse, target: 'list' | 'stats' | 'both') => {
      const rows = normalizePlatformUsers(data);
      if (target === 'list' || target === 'both') {
        setUsers(rows);
        setTotalAdmins(Number(data.totalAdmins ?? rows.filter((u) => u.userType === 'ADMIN').length));
        setTotalMerchants(Number(data.totalMerchants ?? rows.filter((u) => u.userType === 'MERCHANT').length));
      }
      if (target === 'stats' || target === 'both') {
        setStatsUsers(rows);
      }
      return rows;
    },
    [],
  );

  const loadStatsPlatformUsers = useCallback(async () => {
    try {
      const data = await fetchStatsPlatformUsers();
      applyPlatformUserResponse(data, 'stats');
    } catch (err) {
      console.error('Failed to fetch user stats:', err);
    }
  }, [applyPlatformUserResponse]);

  const loadPlatformUsers = useCallback(async () => {
    const nativeColumnFilters = mapAgGridFilterModelToUserFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    const data = await fetchPlatformUsers({
      filterStatus: tableFiltersRef.current.filterStatus,
      filterRoleId: tableFiltersRef.current.filterRoleId,
      search: tableFiltersRef.current.search,
      columnFilters: {
        ...tableFiltersRef.current.columnFilters,
        ...nativeColumnFilters,
      },
    });
    applyPlatformUserResponse(data, 'list');
  }, [filterStatus, filterRoleId, debouncedSearch, columnFilters, gridFilterEpoch, applyPlatformUserResponse]);

  useEffect(() => {
    tableFiltersRef.current = {
      filterStatus,
      filterRoleId,
      search: debouncedSearch,
      columnFilters,
    };
  }, [filterStatus, filterRoleId, debouncedSearch, columnFilters]);

  const refreshPlatformUsers = useCallback(async () => {
    // if (isMerchantUser) {
    //   await loadPlatformUsers();
    //   return;
    // }
    await Promise.all([loadPlatformUsers(), loadStatsPlatformUsers()]);
  }, [loadPlatformUsers, loadStatsPlatformUsers]);

  const handleExport = async () => {
    setExporting(true);
    try {
      const nativeColumnFilters = mapAgGridFilterModelToUserFilters(
        gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
      );
      await downloadExcelExport({
        type: EXPORT_TYPES.USERS,
        fileNamePrefix: 'platform-users',
        errorMessage: 'Failed to export users.',
        payload: buildPlatformUserRequest({
          filterStatus,
          filterRoleId,
          search: debouncedSearch,
          columnFilters: {
            ...columnFilters,
            ...nativeColumnFilters,
          },
        }),
      });
    } finally {
      setExporting(false);
    }
  };

  useAsyncEffect(async (isActive) => {
    setRolesListLoading(true);
    try {
      const roles = await fetchRolesList(authUser?.adminType);
      if (!isActive()) return;
      setFilterRoles(roles);
    } catch (err: unknown) {
      if (!isActive()) return;
      console.error('Failed to load filter roles', err);
    } finally {
      if (isActive()) {
        setRolesListLoading(false);
      }
    }
  }, [authUser?.adminType]);

  useAsyncEffect(async (isActive) => {
    // Merchants provision users under their own account: no merchant picker / list needed.
    // if (isMerchantUser) {
    //   setMerchants([]);
    //   setMerchantsLoading(false);
    //   return;
    // }
    setMerchantsLoading(true);
    try {
      const list = await fetchActiveMerchants();
      if (!isActive()) return;
      setMerchants(list);
    } catch (err: unknown) {
      if (!isActive()) return;
      console.error('Failed to load merchants', err);
    } finally {
      if (isActive()) {
        setMerchantsLoading(false);
      }
    }
  }, []);

  useAsyncEffect(async (isActive) => {
    // KPI cards are admin-only; still load for filter tab counts when shown.
    // if (isMerchantUser) return;
    await loadStatsPlatformUsers();
    if (!isActive()) return;
  }, [loadStatsPlatformUsers]);

  useAsyncEffect(async (isActive) => {
    if (skipRefetchRef.current) {
      skipRefetchRef.current = false;
      return;
    }
    const isInitialLoad = !hasLoadedOnceRef.current;
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setIsRefetching(true);
    }
    setListError(null);

    try {
      await loadPlatformUsers();
      if (!isActive()) return;
    } catch (err: unknown) {
      if (!isActive()) return;
      setUsers([]);
      setTotalAdmins(0);
      setTotalMerchants(0);
      setListError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to load users.'
      );
    } finally {
      if (!isActive()) return;
      if (isInitialLoad) {
        setLoading(false);
        hasLoadedOnceRef.current = true;
      } else {
        setIsRefetching(false);
      }
    }
  }, [loadPlatformUsers]);

  useEffect(() => {
    setExpandedRowKey(null);
    gridApiRef.current?.paginationGoToFirstPage();
  }, [filterStatus, filterRoleId, debouncedSearch, columnFilters, gridFilterEpoch]);

  useEffect(() => {
    if (!isCreateMode || !userModalMode || merchantsLoading || merchants.length > 0) {
      return;
    }
    setModalError('No active merchants found. Please create a merchant first.');
  }, [isCreateMode, userModalMode, merchants, merchantsLoading]);

  useEffect(() => {
    if (!isEditMode || !userForm.id || merchants.length === 0 || userForm.merchantId) {
      return;
    }

    const user = users.find(
      (row) => row.id === userForm.id && row.userType === userForm.userType,
    );
    if (!user) return;

    const merchantId = resolveMerchantIdForCompany(user.company, merchants);
    if (merchantId) {
      setUserForm((prev) => ({ ...prev, merchantId }));
    }
  }, [isEditMode, merchants, userForm.id, userForm.userType, userForm.merchantId, users]);

  const handleEdit = async (idx: number) => {
    const user = users[idx];
    if (!user || isSuperAdminRow(user)) return;

    setModalError(null);
    setRolesLoading(true);

    try {
      const roles = filterRoles.length > 0
        ? filterRoles
        : await fetchRolesList(authUser?.adminType);
      setModalRoles(roles);
      const merchantId = resolveMerchantIdForCompany(user.company, merchants);
      setUserForm({
        ...EMPTY_USER_FORM,
        id: user.id,
        userType: user.userType,
        name: user.name,
        email: user.email,
        roleId: user.roleId ?? 0,
        merchantId,
        status: user.status,
        checkoutLinkEmailEnabled: user.checkoutLinkEmailEnabled ?? true,
        liveDccEnabled: user.liveDccEnabled ?? false,
        merchantIds: (user.assignedMerchantIds ?? []).map(String),
        twoFactorEnabled: user.twoFactorEnabled ?? true,
        dashboardKey: resolveMerchantDashboardKey(user.dashboardKey),
      });
      setUserModalMode('edit');

      if (user.userType === 'MERCHANT' && merchantId) {
        try {
          const res = await apiService.get(`${API_PATHS.GET_MERCHANT}/${merchantId}`);
          setUserForm((prev) => ({
            ...prev,
            dashboardKey: resolveMerchantDashboardKey(res),
          }));
        } catch (error) {
          console.error('Failed to load merchant dashboard key:', error);
        }
      }
    } catch (err: unknown) {
      setModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to load roles. Please try again.'
      );
    } finally {
      setRolesLoading(false);
    }
  };

  const handleUserFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = 'checked' in e.target ? e.target.checked : false;
    if (name === 'roleId') {
      setUserForm((prev) => ({ ...prev, roleId: Number(value) || 0 }));
      setModalError(null);
      return;
    }
    if (type === 'checkbox') {
      setUserForm((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setUserForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setUserModalMode(null);
    setModalError(null);
    setShowPassword(false);
  };

  const handleModalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userModalMode) return;

    setModalError(null);
    setModalSaving(true);

    const emailError = validateEmail(String(userForm.email ?? ''), { required: true });
    if (emailError) {
      setModalError(emailError);
      setModalSaving(false);
      return;
    }

    try {
      if (isCreateMode) {
        if (!userForm.roleId) {
          setModalError('Please select a role.');
          return;
        }

        if (needsMerchantId && !userForm.merchantId.trim()) {
          setModalError('Please select a merchant for this role.');
          return;
        }

        if (!isAuthenticated || !authUser) {
          setModalError('You must be logged in to invite users.');
          return;
        }

        const invokerId = authUser.id;
        if (!invokerId || invokerId <= 0) {
          setModalError('Your session is missing admin ID. Please log out and sign in again.');
          return;
        }

        const payload: ProvisionUserRequest = {
          invokerId,
          roleId: userForm.roleId,
          name: userForm.name.trim(),
          email: userForm.email.trim(),
          password: userForm.password,
          sendNotification: userForm.sendNotification,
        };

        if (needsMerchantId) {
          payload.merchantId = Number(userForm.merchantId);
          if (!payload.merchantId || Number.isNaN(payload.merchantId)) {
            setModalError('Please select a valid merchant.');
            return;
          }
          payload.dashboardKey = resolveMerchantDashboardKey(userForm.dashboardKey);
        }

        if (isReferralEligibleRole) {
          if (userForm.merchantIds.length === 0) {
            setModalError('Please select at least one merchant for this admin.');
            return;
          }
          payload.merchantIds = userForm.merchantIds.map(Number);
        }

        if (isSuperAdminCaller) {
          payload.twoFactorEnabled = userForm.twoFactorEnabled;
        }

        await provisionUser(payload);
      } else {
        const payload: PlatformUserUpdateRequest = {
          userId: Number(userForm.id),
          userType: String(userForm.userType),
          name: String(userForm.name ?? '').trim(),
          email: String(userForm.email ?? '').trim(),
          roleId: userForm.roleId ? Number(userForm.roleId) : undefined,
          status: toApiStatus(String(userForm.status ?? '')),
        };

        if (String(userForm.userType) === 'MERCHANT') {
          payload.checkoutLinkEmailEnabled = userForm.checkoutLinkEmailEnabled;
          payload.liveDccEnabled = userForm.liveDccEnabled;
          payload.dashboardKey = resolveMerchantDashboardKey(userForm.dashboardKey);
        }

        if (String(userForm.userType) === 'ADMIN' && isReferralEligibleRole) {
          if (userForm.merchantIds.length === 0) {
            setModalError('Please select at least one merchant for this admin.');
            return;
          }
          payload.merchantIds = userForm.merchantIds.map(Number);
        }

        if (isSuperAdminCaller) {
          payload.twoFactorEnabled = userForm.twoFactorEnabled;
        }

        await updatePlatformUser(payload);
      }

      handleCloseModal();
      await refreshPlatformUsers();
    } catch (err: unknown) {
      setModalError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        (isCreateMode ? 'Unable to provision user.' : 'Unable to update user.')
      );
    } finally {
      setModalSaving(false);
    }
  };

  const handleToggleStatus = async (idx: number) => {
    const user = users[idx];
    if (!user || isSuperAdminRow(user)) return;
    if (authUser?.id === user.id && user.userType === 'ADMIN') {
      setListError('You cannot deactivate your own account.');
      return;
    }

    const key = userRowKey(user);
    const nextStatus = user.status === 'Active' ? 'INACTIVE' : 'ACTIVE';
    setListError(null);
    setTogglingUserKey(key);

    try {
      const payload: PlatformUserUpdateRequest = {
        userId: user.id,
        userType: user.userType,
        status: nextStatus,
      };

      await updatePlatformUser(payload);

      await refreshPlatformUsers();
    } catch (err: unknown) {
      setListError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to update user status.'
      );
    } finally {
      setTogglingUserKey(null);
    }
  };

  const handleToggleSandboxMode = async (idx: number) => {
    const user = users[idx];
    if (!user || user.userType !== 'MERCHANT') return;

    const key = userRowKey(user);
    const nextEnabled = !user.sandboxModeEnabled;
    setListError(null);
    setTogglingSandboxKey(key);

    try {
      const payload: PlatformUserUpdateRequest = {
        userId: user.id,
        userType: user.userType,
        sandboxModeEnabled: nextEnabled,
      };

      await updatePlatformUser(payload);

      await refreshPlatformUsers();
    } catch (err: unknown) {
      setListError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to update sandbox mode.'
      );
    } finally {
      setTogglingSandboxKey(null);
    }
  };

  const handleImpersonate = async (user: PlatformUserRow) => {
    if (user.userType !== 'MERCHANT' && user.userType !== 'ADMIN') return;

    const key = userRowKey(user);
    setListError(null);
    setImpersonatingKey(key);

    try {
      const data = await impersonateUser(user.id, user.userType);
      const { sessionToken, ...sessionData } = data;
      const encoded = encodeImpersonationPayload({ ...sessionData, token: sessionToken });
      window.open(`${window.location.origin}/impersonate#impersonate=${encoded}`, '_blank');
    } catch (err: unknown) {
      setListError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to start impersonation session.'
      );
    } finally {
      setImpersonatingKey(null);
    }
  };

  const handleToggleExpand = useCallback(async (user: PlatformUserRow) => {
    const rowKey = userRowKey(user);
    if (expandedRowKey === rowKey) {
      setExpandedRowKey(null);
      return;
    }

    setExpandedRowKey(rowKey);
    if (recentLoginsByKey[rowKey]) {
      return;
    }

    setRecentLoginsError(null);
    setRecentLoginsLoadingKey(rowKey);
    try {
      const logins = await fetchRecentLogins(user.id, user.userType);
      setRecentLoginsByKey(prev => ({ ...prev, [rowKey]: logins }));
    } catch (err: unknown) {
      setRecentLoginsError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err as Error)?.message ||
        'Unable to load recent logins.'
      );
    } finally {
      setRecentLoginsLoadingKey(null);
    }
  }, [expandedRowKey, recentLoginsByKey]);

  const handleUserRowExpandClick = useCallback((data: UserGridRow, event?: Event | null) => {
    if (data.rowType !== 'user') return;

    const target = event?.target as HTMLElement | null;
    if (target?.closest('button, a, input, [role="switch"], [role="combobox"]')) {
      return;
    }

    const user = users.find((u) => userRowKey(u) === data.rowKey);
    if (user) {
      void handleToggleExpand(user);
    }
  }, [users, handleToggleExpand]);

  const modalRoleOptions = useMemo(() => {    
    if (!isEditMode) {
      return modalRoles;
    }
    return modalRoles.filter((role) => {
      if (String(userForm.userType ?? '').toUpperCase() === 'MERCHANT') {
        return role.roleScope === 'MERCHANT';
      }
      return role.roleScope === 'ADMIN';
    });
  }, [isEditMode, modalRoles, userForm.userType]);

  const roleFilterSelectOptions = useMemo(
    () => [
      { value: 'All', label: 'All Roles' },
      ...toRoleSelectOptions(filterRoles),
    ],
    [filterRoles],
  );

  const modalRoleSelectOptions = useMemo(
    () => toRoleSelectOptions(modalRoleOptions),
    [modalRoleOptions],
  );

  const merchantSelectOptions = useMemo(
    () =>
      merchants.map((merchant) => ({
        value: String(merchant.autoId),
        label: `${merchant.name}${merchant.email ? ` (${merchant.email})` : ''}`,
        searchText: `${merchant.name} ${merchant.email ?? ''}`,
      })),
    [merchants],
  );

  const editingUserCompany = useMemo(() => {
    if (!isEditMode || !userForm.id) return '';
    const user = users.find(
      (row) => row.id === userForm.id && row.userType === userForm.userType,
    );
    const company = user?.company?.trim() ?? '';
    return company && company !== '—' ? company : '';
  }, [isEditMode, userForm.id, userForm.userType, users]);

  const fetchRolesForModal = async (): Promise<RoleOption[]> => {
    if (filterRoles.length > 0) {
      return filterRoles;
    }
    const roles = await fetchRolesList(authUser?.adminType);
    if (roles.length === 0) {
      throw new Error('No roles available for your account.');
    }
    return roles;
  };

  const handleOpenCreate = async () => {
    setModalError(null);
    setRolesLoading(true);

    try {
      const roles = await fetchRolesForModal();
      if (roles.length === 0) {
        throw new Error('No roles available for your account.');
      }
      setModalRoles(roles);
      setUserForm({
        ...EMPTY_USER_FORM,
        roleId: getDefaultMerchantRoleId(roles),
        merchantId: '',
      });
      setUserModalMode('create');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } }; message?: string })?.response?.data?.message ||
        (err as Error)?.message ||
        'Failed to load roles. Please try again.';
      setModalError(message);
    } finally {
      setRolesLoading(false);
    }
  };

  const handleGridFilterChanged = useCallback(() => {
    setGridFilterEpoch((n) => n + 1);
  }, []);

  const handleGridReady = useCallback((event: { api: GridApi<UserGridRow> }) => {
    gridApiRef.current = event.api;
  }, []);

  const mergedColumnFilters = useMemo<UserColumnFilters>(() => {
    void gridFilterEpoch;
    const nativeColumnFilters = mapAgGridFilterModelToUserFilters(
      gridApiRef.current?.getFilterModel() as Record<string, unknown> | null | undefined,
    );
    return {
      ...columnFilters,
      ...nativeColumnFilters,
    };
  }, [columnFilters, gridFilterEpoch]);

  const activeColumnFilters = useMemo<ActiveGridFilter[]>(() => {
    return (Object.entries(mergedColumnFilters) as [UserColumnFilterKey, string[] | null][])
      .filter(([, value]) => Boolean(value?.length))
      .map(([key, value]) => ({
        key,
        label: USER_COLUMN_LABELS[key],
        value: (value as string[]).join(', '),
      }));
  }, [mergedColumnFilters]);

  const columnFilterFields = useMemo(() => {
    const fields = buildUserColumnFilterFields(
      statsUsers.length === 0 ? users : statsUsers,
    );
    return fields.map((field) => {
      const applied = mergedColumnFilters[field.key as UserColumnFilterKey];
      if (!applied?.length) return field;
      const missing = applied.filter((value) => !field.options.includes(value));
      if (!missing.length) return field;
      return {
        ...field,
        options: [...field.options, ...missing],
      };
    });
  }, [users, statsUsers, mergedColumnFilters]);

  const activeCount = statsUsers.filter((u) => u.status === 'Active').length;
  const adminCount = totalAdmins || statsUsers.filter((u) => u.userType === 'ADMIN').length;
  const pendingCount = statsUsers.filter((u) => u.status === 'Pending').length;
  const departmentCount = [
    ...new Set(statsUsers.map((u) => u.department).filter((d) => d && d !== '—')),
  ].length;

  const kpis = [
    { label: 'Total Users',     value: loading ? '…' : statsUsers.length.toString(),    change: `${totalAdmins} admins · ${totalMerchants} merchants`, color: '#5c6bc0', icon: <BsPeopleFill /> },
    { label: 'Active',          value: loading ? '…' : activeCount.toString(),      change: statsUsers.length ? `${activeCount}/${statsUsers.length}` : '0 users', color: '#2ed573', icon: <BsCheckCircle /> },
    { label: 'Admins',          value: loading ? '…' : adminCount.toString(),       change: 'Platform admins', color: '#ff4757', icon: <BsShieldFill /> },
    { label: 'Merchant Users',  value: loading ? '…' : totalMerchants.toString(),   change: 'Merchant sub-users', color: '#26a69a', icon: <BsShop /> },
    { label: 'Pending Invites', value: loading ? '…' : pendingCount.toString(),     change: 'Awaiting login', color: '#ffa502', icon: <BsEnvelopeFill /> },
    { label: 'Departments',     value: loading ? '…' : departmentCount.toString(), change: 'Across org', color: '#a78bfa', icon: <BsBuilding /> },
  ];

  const statusTabs = ENTITY_STATUS_TABS;

  const userGridRows = useMemo(
    () =>
      buildUserGridRows(
        users,
        users,
        expandedRowKey,
        recentLoginsByKey,
        recentLoginsLoadingKey,
        recentLoginsError,
      ),
    [users, expandedRowKey, recentLoginsByKey, recentLoginsLoadingKey, recentLoginsError],
  );

  useEffect(() => {
    const api = gridApiRef.current
    if (!api) return
    const frame = window.requestAnimationFrame(() => {
      api.resetRowHeights()
      api.onRowHeightChanged()
    })
    return () => window.cancelAnimationFrame(frame)
  }, [recentLoginsByKey, recentLoginsLoadingKey, recentLoginsError, expandedRowKey])

  useEffect(() => {
    if (!expandedRowKey) return;

    const api = gridApiRef.current;
    if (!api) return;

    const frame = window.requestAnimationFrame(() => {
      api.forEachNode((node) => {
        if (node.data?.rowType === 'detail' && node.data.rowKey === expandedRowKey) {
          api.ensureIndexVisible(node.rowIndex ?? 0, 'bottom');
        }
      });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [expandedRowKey, userGridRows]);

  const userGridContext = useMemo<UserGridContext>(
    () => ({
      users,
      authUserId: authUser?.id,
      isMerchantViewer: false,
      expandedRowKey,
      togglingUserKey,
      togglingSandboxKey,
      impersonatingKey,
      recentLoginsByKey,
      recentLoginsLoadingKey,
      recentLoginsError,
      accentColors: ACCENT_COLORS,
      roleStyle,
      isSuperAdminRow,
      formatRelativeTime,
      onEdit: handleEdit,
      onToggleStatus: handleToggleStatus,
      onToggleSandbox: handleToggleSandboxMode,
      onImpersonate: handleImpersonate,
      onToggleExpand: handleToggleExpand,
    }),
    [
      users,
      authUser?.id,
      false,
      expandedRowKey,
      togglingUserKey,
      togglingSandboxKey,
      impersonatingKey,
      recentLoginsByKey,
      recentLoginsLoadingKey,
      recentLoginsError,
      handleToggleExpand,
    ]
  );

  const userGridOptions = useMemo(
    () => ({
      ...userGridOptionsBase,
      getRowId: (params: { data: UserGridRow }) =>
        params.data.rowType === 'detail'
          ? `detail-${params.data.rowKey}`
          : `user-${params.data.rowKey}`,
      isFullWidthRow: (params: { rowNode: { data?: UserGridRow } }) =>
        params.rowNode.data?.rowType === 'detail',
      fullWidthCellRenderer: UserDetailCellRenderer,
      embedFullWidthRows: true,
      getRowHeight: (params: { data?: UserGridRow }) => getUserDetailRowHeight(params.data),
      getRowClass: (params: { data?: UserGridRow }) => {
        if (params.data?.rowType === 'detail') return 'um-detail-row';
        if (params.data?.rowType !== 'user') return '';
        return expandedRowKey === params.data.rowKey ? 'ag-row-expanded' : '';
      },
      onRowClicked: (event: { data?: UserGridRow; event?: Event | null }) => {
        if (!event.data) return;
        handleUserRowExpandClick(event.data, event.event ?? null);
      },
      rowClass: 'cursor-pointer',
      onFilterChanged: handleGridFilterChanged,
    }),
    [expandedRowKey, handleUserRowExpandClick, handleGridFilterChanged]
  );

  const userColumnDefs = useMemo(() => createUserColumnDefs(), []);

  const pageError = listError || (!userModalMode && modalError ? modalError : null);

  return (
    <ListConsolePage
      title="User Management"
      error={pageError}
      actions={
        <Button
          type="button"
          size="sm"
          className="tm-console-action-btn gap-1.5"
          onClick={() => void handleOpenCreate()}
          disabled={rolesLoading}
        >
          <BsPlusCircle className="h-3 w-3 shrink-0" aria-hidden />
          {rolesLoading ? 'Loading roles…' : 'Add User'}
        </Button>
      }
    >

      {/* Stat Cards: admin overview only */}
          {false && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
          {kpis.map((k, i) => (
            <Card key={i} className="relative overflow-hidden">
              <div
                className="absolute inset-x-0 top-0 h-1"
                style={{ background: k.color }}
              />
              <CardContent className="flex flex-col gap-1 p-4 pt-5">
                <span
                  className="mb-1 inline-flex h-9 w-9 items-center justify-center rounded-full text-base"
                  style={{
                    background: `color-mix(in srgb, ${k.color} 16%, transparent)`,
                    color: k.color,
                  }}
                >
                  {k.icon}
                </span>
                <div className="text-2xl font-bold">{k.value}</div>
                <div className="text-sm font-semibold">{k.label}</div>
                <div className="text-xs text-muted-foreground">{k.change}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ListConsoleGridSection
        toolbarLeft={
          <StatusFilterTabs
            tabs={statusTabs}
            value={filterStatus}
            onChange={setFilterStatus}
            disabled={loading || isRefetching}
            getCount={(tab) => {
              const key = typeof tab === 'string' ? tab : tab.value;
              const source = statsUsers;
              return key === 'All'
                ? source.length
                : source.filter((u) => matchesEntityStatus(u.status, key)).length;
            }}
          />
        }
        toolbarRight={
          <div className="tm-toolbar-controls">
            <div className="tm-toolbar-filter-cluster">
              <div className="tm-toolbar-role-field">
                <SearchableSelect
                  className="tm-toolbar-select"
                  value={filterRoleId}
                  options={roleFilterSelectOptions}
                  onChange={setFilterRoleId}
                  placeholder="All Roles"
                  searchPlaceholder="Search role…"
                  disabled={rolesListLoading}
                  aria-label="Filter by role"
                />
              </div>
              <GridSearchInput
                value={search}
                onValueChange={setSearch}
                placeholder="Search name, email or department..."
                className="tm-toolbar-search"
                disabled={loading || isRefetching}
              />
              <GridColumnFilterButton
                activeCount={activeColumnFilters.length}
                onClick={() => setFilterPanelOpen(true)}
                disabled={loading || isRefetching}
                className="tm-compact-icon-btn"
              />
              <GridExportButton
                onClick={handleExport}
                loading={exporting}
                disabled={loading || isRefetching || users.length === 0}
                className="tm-compact-icon-btn"
              />
            </div>
          </div>
        }
        filterBar={
          <ActiveGridFiltersBar
            filters={activeColumnFilters}
            onRemove={handleRemoveColumnFilter}
            onClearAll={handleClearColumnFilters}
          />
        }
      >
        <DataGrid<UserGridRow>
          rowData={userGridRows}
          columnDefs={userColumnDefs}
          context={userGridContext}
          loading={loading || isRefetching}
          pagination
          paginationPageSize={GRID_PAGE_SIZE}
          paginationPageSizeSelector={GRID_PAGE_SIZE_SELECTOR}
          height="100%"
          className={LIST_CONSOLE_AG_GRID_CLASS}
          onGridReady={handleGridReady}
          defaultColDef={USER_GRID_DEFAULT_COL_DEF}
          gridOptions={userGridOptions}
        />
      </ListConsoleGridSection>

      <Dialog open={!!userModalMode} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{isCreateMode ? 'Add User' : 'Edit User'}</DialogTitle>
            <DialogDescription>
              {isCreateMode
                ? false
                  ? 'Invite a new user under your merchant account.'
                  : 'Provision a new platform or merchant user.'
                : 'Update user details and account status.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleModalSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="user-name">Name</Label>
                <Input
                  id="user-name"
                  name="name"
                  value={userForm.name}
                  onChange={handleUserFormChange}
                  required
                  placeholder="Enter name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  name="email"
                  type="email"
                  value={userForm.email}
                  onChange={handleUserFormChange}
                  required
                  placeholder="Enter email"
                />
              </div>
                {!isReferralEligibleRole && (
                <div className="space-y-2">
                  <Label>
                    Merchant{needsMerchantId && isCreateMode ? ' *' : ''}
                  </Label>
                  <SearchableSelect
                    className="h-9"
                    value={String(userForm.merchantId || '')}
                    options={merchantSelectOptions}
                    onChange={(value) => handleUserFormChange({ target: { name: 'merchantId', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    placeholder={
                      merchantsLoading && editingUserCompany
                        ? editingUserCompany
                        : merchantsLoading
                          ? 'Loading merchants…'
                          : 'Select a merchant'
                    }
                    renderValue={(option) => {
                      if (option && option.label !== option.value) return option.label;
                      return editingUserCompany || option?.label;
                    }}
                    searchPlaceholder="Search merchant…"
                    emptyMessage="No merchants available"
                    menuPortal={false}
                    aria-label="Merchant"
                  />
                </div>
              )}
              {isReferralEligibleRole && (
                <div className="space-y-2 sm:col-span-2">
                  <Label>Assigned merchants *</Label>
                  <p className="text-xs text-muted-foreground">
                    Every admin is scoped to specific merchants they can only see the merchants
                    selected below.
                  </p>
                  <SearchableMultiSelect
                    className="h-9"
                    value={userForm.merchantIds}
                    options={merchantSelectOptions}
                    onChange={(value) => setUserForm((prev) => ({ ...prev, merchantIds: value }))}
                    placeholder={merchantsLoading ? 'Loading merchants…' : 'Select merchants'}
                    searchPlaceholder="Search merchant…"
                    emptyMessage="No merchants available"
                    aria-label="Assigned merchants"
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label>Role{isCreateMode ? ' *' : ''}</Label>
                <SearchableSelect
                  className="h-9"
                  value={String(userForm.roleId || '')}
                  options={modalRoleSelectOptions}
                  onChange={(value) => handleUserFormChange({ target: { name: 'roleId', value } } as React.ChangeEvent<HTMLSelectElement>)}
                  placeholder="Select role"
                  searchPlaceholder="Search role…"
                  emptyMessage="No roles available"
                  disabled={isEditMode || modalRoleOptions.length === 0}
                  menuPortal={false}
                  aria-label="Role"
                />
              </div>
              {isCreateMode && (
                <div className="space-y-2 sm:col-span-2">
                  <Label htmlFor="user-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="user-password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={userForm.password}
                      onChange={handleUserFormChange}
                      required
                      minLength={6}
                      placeholder="Enter password"
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                      onClick={() => setShowPassword(v => !v)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <BsEyeSlash /> : <BsEye />}
                    </Button>
                  </div>
                </div>
              )}
              {isEditMode && (
                <div className="space-y-2">
                  <Label>Status</Label>
                  <SearchableSelect
                    className="h-9"
                    value={userForm.status ?? ''}
                    options={ENTITY_STATUS_SELECT_OPTIONS}
                    onChange={(value) => handleUserFormChange({ target: { name: 'status', value } } as React.ChangeEvent<HTMLSelectElement>)}
                    placeholder="Select status"
                    searchPlaceholder="Search status…"
                    menuPortal={false}
                    aria-label="Status"
                  />
                </div>
              )}

              {((isCreateMode && needsMerchantId) || (isEditMode && userForm.userType === 'MERCHANT')) && (
                <div className="space-y-2">
                  <Label>Merchant Dashboard Style</Label>
                  <SearchableSelect
                    className="h-9"
                    value={resolveMerchantDashboardKey(userForm.dashboardKey)}
                    options={[...MERCHANT_DASHBOARD_KEY_OPTIONS]}
                    onChange={(value) =>
                      setUserForm((prev) => ({
                        ...prev,
                        dashboardKey: resolveMerchantDashboardKey(value),
                      }))
                    }
                    placeholder="Select dashboard"
                    searchPlaceholder="Search dashboard…"
                    emptyMessage="No dashboard options available"
                    menuPortal={false}
                    aria-label="Merchant dashboard"
                  />
                </div>
              )}

              {isSuperAdminCaller && (
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4 sm:col-span-2">
                  <div className="space-y-0.5">
                    <Label>Two-factor authentication</Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, an OTP is emailed and required at login. When disabled, email
                      and password alone are enough to log in.
                    </p>
                  </div>
                  <Switch
                    checked={userForm.twoFactorEnabled}
                    onCheckedChange={(checked) =>
                      setUserForm((prev) => ({ ...prev, twoFactorEnabled: checked }))
                    }
                    aria-label="Toggle two-factor authentication"
                  />
                </div>
              )}

              {isEditMode && userForm.userType === 'MERCHANT' && (
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4 sm:col-span-2">
                  <div className="space-y-0.5">
                    <Label>Checkout link email</Label>
                    <p className="text-xs text-muted-foreground">
                      When enabled, customers get a "complete your payment" email.
                    </p>
                  </div>
                  <Switch
                    checked={userForm.checkoutLinkEmailEnabled}
                    onCheckedChange={(checked) =>
                      setUserForm((prev) => ({ ...prev, checkoutLinkEmailEnabled: checked }))
                    }
                    aria-label="Toggle checkout link email"
                  />
                </div>
              )}

                {isEditMode && userForm.userType === 'MERCHANT' && (
                <div className="flex items-center justify-between gap-4 rounded-lg border p-4 sm:col-span-2">
                  <div className="space-y-0.5">
                    <Label>Automatic DCC</Label>
                    <p className="text-xs text-muted-foreground">
                      Automatically converts unsupported currencies using live exchange rates.
                    </p>
                  </div>
                  <Switch
                    checked={userForm.liveDccEnabled}
                    onCheckedChange={(checked) =>
                      setUserForm((prev) => ({ ...prev, liveDccEnabled: checked }))
                    }
                    aria-label="Toggle automatic DCC"
                  />
                </div>
              )}
            </div>

            {isCreateMode && (
              <div className={cn(
                "flex items-center justify-between gap-4 rounded-lg border p-4",
                userForm.sendNotification && "border-primary/30 bg-primary/5"
              )}>
                <div className="flex gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <BsEnvelopeFill />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-semibold">Send invite email</p>
                    <p className="text-xs text-muted-foreground">
                      {userForm.sendNotification
                        ? 'The user will receive an email with login credentials from the notification template.'
                        : 'The user will be created without an email. Share credentials manually.'}
                    </p>
                    <Badge variant={userForm.sendNotification ? 'default' : 'secondary'} className="text-xs">
                      {userForm.sendNotification ? 'Notification enabled' : 'Notification disabled'}
                    </Badge>
                  </div>
                </div>
                <Switch
                  checked={userForm.sendNotification}
                  onCheckedChange={(checked) =>
                    handleUserFormChange({
                      target: { name: 'sendNotification', type: 'checkbox', checked },
                    } as React.ChangeEvent<HTMLInputElement>)
                  }
                  aria-label="Send invite email with login credentials"
                />
              </div>
            )}

            {modalError && (
              <Alert variant="destructive">
                <AlertDescription>{modalError}</AlertDescription>
              </Alert>
            )}

            {isCreateMode && (
              <Alert>
                <AlertDescription>
                  {false
                    ? 'New users are created under your merchant account. Select a role to set their access.'
                    : 'Roles are loaded from the server when you open this form. Select a role to see provisioning requirements.'}
                </AlertDescription>
              </Alert>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseModal} disabled={modalSaving}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={modalSaving || rolesLoading || (isCreateMode && needsMerchantId && merchantsLoading)}
              >
                {modalSaving
                  ? (isCreateMode ? 'Provisioning…' : 'Saving…')
                  : (isCreateMode ? 'Send Invite' : 'Save Changes')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <GridColumnFilterPanel
        open={filterPanelOpen}
        onOpenChange={setFilterPanelOpen}
        fields={columnFilterFields}
        values={mergedColumnFilters}
        multiple
        onApply={handleApplyColumnFilters}
        onClear={handleClearColumnFilters}
      />
    </ListConsolePage>
  );
};

export default UserManagement;
